import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import { useViewport } from 'hooks/3catinfo/useViewport';
import { useEleccions } from 'hooks/3catinfo/useEleccions';
import { normalizeText } from '@portal-internet/core';
import clsx from 'clsx';
import __isEmpty from 'lodash/isEmpty';
import Imatge from 'components/3catinfo/Imatge';
import Text from 'components/3catinfo/Text';
import Boto from 'components/3catinfo/Boto';
import TabSelector from 'components/3catinfo/Tabs/TabSelector';
import GraficBarraHoritzontal from 'components/3catinfo/Eleccions/Grafiques/TipusGrafics/GraficBarraHoritzontal';
import styles from './pactometre.module.scss';

const SelectButton = ({ options, id, current, onChange, isEmpty }) => {
    return (
        <div className={styles.selectBox}>
            {options.map((opt) => (
                <button
                    key={opt.label}
                    onClick={() => {
                        !isEmpty && onChange(id, current === opt.value ? null : opt.value);
                    }}
                    className={`${styles.selectButton} ${current === opt.value ? styles.selected : ''}`}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );
};

const optionsButtonsMunicipals = [{ label: 'Pacte', value: 'Si' }];
const optionsButtonsAutonomiques = [
    { label: 'Sí', value: 'Si' },
    //{ label: 'Abs', value: 'Abs' },
    { label: 'No', value: 'No' }
];

const getCandidat = (partits = [], selections, type) => {
    const partitsFiltrats = partits.filter((partit) => selections?.[partit.id] === type);
    if (__isEmpty(partitsFiltrats)) return { candidat: '', num: 0 };

    const maxMnumitems = Math.max(...partitsFiltrats.map((p) => p.mnumitems));
    const partitsMax = partitsFiltrats.filter((p) => p.mnumitems === maxMnumitems);

    let candidat = '';
    if (partitsMax.length === 1) {
        candidat = partitsMax[0]?.candidat?.nom ? `${partitsMax[0].candidat.nom}` : `El candidat de ${partitsMax[0].id}`;
    } else {
        candidat = partitsMax.map((p, idx) => `${idx === 0 ? 'El candidat de' : 'el candidat de'} ${p.id}`).join(' i ');
    }
    return { candidat, num: partitsMax.length, fotoCandidat: partitsMax?.[0]?.candidat?.foto };
};

const getMessagesText = (tipusPactometre, keyMessage, params) => {
    const texts = {
        generals: {
            msgVotsSi: `<strong>${params?.candidat}</strong> podria accedir a la presidència amb majoria absoluta`,
            msgVotsSiVaris: `<strong>${params?.candidat}</strong> podrien accedir a la presidència amb majoria absoluta`,
            msgVotsSiMajorVotsNo: `<strong>${params?.candidat}</strong> podria accedir a la presidència en segona votació amb majoria simple`,
            msgVotsSiMajorVotsNoVaris: `<strong>${params?.candidat}</strong> podrien accedir a la presidència en segona votació amb majoria simple`,
            msgVotsSiIgualVotsNo: `Si empaten el "Sí" i el "No" comença el període de dos mesos per fer una altra investidura o repetir eleccions'`,
            msgVotsNoMajor: `Si guanya el "No" comença el perí­ode de dos mesos per fer una altra investidura o repetir eleccions`
        },
        autonomiques: {
            msgVotsSi: `<strong>${params?.candidat}</strong> podria accedir a la presidència amb majoria absoluta`,
            msgVotsSiVaris: `<strong>${params?.candidat}</strong> podrien accedir a la presidència amb majoria absoluta`,
            msgVotsSiMajorVotsNo: `<strong>${params?.candidat}</strong> podria accedir a la presidència en segona votació amb majoria simple`,
            msgVotsSiMajorVotsNoVaris: `<strong>${params?.candidat}</strong> podrien accedir a la presidència en segona votació amb majoria simple`,
            msgVotsSiIgualVotsNo: `Si empaten el "Sí" i el "No" comença el període de dos mesos per fer una altra investidura o repetir eleccions'`,
            msgVotsNoMajor: `Si guanya el "No" comença el perí­ode de dos mesos per fer una altra investidura o repetir eleccions`
        },
        municipals: {
            msgMajoriaAbsolutaPartit: `<strong>${params?.partit}</strong> guanyarà l'alcaldia`,
            msgMajoriaAbsolutaCandidat: `<strong>${params?.candidat}</strong> serà ${params?.title}`,
            msgCondPreferent: `<strong>${params?.candidat}</strong> ${params?.verb} ${params?.title} si no hi ha un pacte alternatiu amb ${params?.regidors} regidors`,
            msgCondNoPreferent: `<strong>${params?.partit}</strong> ${params?.verb} l'alcaldia si no hi ha un pacte alternatiu amb ${params?.regidors} regidors`,
            msgPreferentPacteAlternatiu: `<strong>${params?.candidat}</strong> ${params?.verb} ${params?.title} perquè ha aconseguit un pacte alternatiu a ${params?.candidatAlt} amb ${params?.regidors} regidors`,
            msgPreferentPacte: `<strong>${params?.candidat}</strong> ${params?.verb} ${params?.title} perquè ha aconseguit un pacte amb ${params?.regidors} regidors`,
            msgNoPreferentPacteAlternatiu: `<strong>${params?.candidat}</strong> ${params?.verb} l'alcaldia perquè ha aconseguit un pacte alternatiu a ${params?.candidatAlt} amb ${params?.regidors} regidors`,
            msgNoPreferentPacte: `<strong>${params?.candidat}</strong> ${params?.verb} l'alcaldia perquè ha aconseguit un pacte amb ${params?.regidors} regidors`
        }
    };
    return texts?.[tipusPactometre]?.[keyMessage];
};

const getMessageAutonomiques = (tipus, selections, partits = [], minValue) => {
    let message = {};
    const partitsSi = partits.filter((partit) => selections[partit.id] === 'Si');
    const partitsNo = partits.filter((partit) => selections[partit.id] === 'No');
    const candidatSi = getCandidat(partits, selections, 'Si');
    const totalVotsSi = partits.reduce((acc, partit) => {
        return selections[partit.id] === 'Si' ? acc + partit.mnumitems : acc;
    }, 0);
    const totalVotsNo = partits.reduce((acc, partit) => {
        return selections[partit.id] === 'No' ? acc + partit.mnumitems : acc;
    }, 0);
    if (!__isEmpty(partitsSi) || !__isEmpty(partitsNo)) {
        if (totalVotsSi >= minValue) {
            message = {
                msg: getMessagesText(tipus, 'msgVotsSi', { candidat: candidatSi?.candidat }),
                fotoCandidat: candidatSi?.fotoCandidat
            };
            if (candidatSi.num > 1)
                message = {
                    msg: getMessagesText(tipus, 'msgVotsSiVaris', { candidat: candidatSi?.candidat }),
                    fotoCandidat: candidatSi?.fotoCandidat
                };
        } else if (totalVotsSi > totalVotsNo) {
            message = {
                msg: getMessagesText(tipus, 'msgVotsSiMajorVotsNo', { candidat: candidatSi?.candidat }),
                fotoCandidat: candidatSi?.fotoCandidat
            };
            if (candidatSi.num > 1)
                message = {
                    msg: getMessagesText(tipus, 'msgVotsSiMajorVotsNoVaris', { candidat: candidatSi?.candidat }),
                    fotoCandidat: candidatSi?.fotoCandidat
                };
        } else if (totalVotsSi === totalVotsNo) {
            message = {
                msg: getMessagesText(tipus, 'msgVotsSiIgualVotsNo')
            };
        } else if (totalVotsNo > minValue || totalVotsNo > totalVotsSi) {
            message = {
                msg: getMessagesText(tipus, 'msgVotsNoMajor')
            };
        }
    }
    return message;
};

const getMessageCandidat = ({
    tipus,
    candidate,
    partitId = null,
    isPreferent,
    isFinal,
    regidors = null,
    isPacte = false,
    altCandidateName = '',
    conditional = false,
    nomCandidatsMultiple = null,
    nomPartitsMultiple = null
}) => {
    const futureVerb = nomCandidatsMultiple ? (isFinal ? 'seran' : 'serien') : isFinal ? 'serà' : 'seria';
    const futureVerbAlt = isFinal ? 'guanyarà' : 'guanyaria';
    const title = nomCandidatsMultiple ? 'alcaldes' : candidate?.genere === 'DONA' ? 'alcaldessa' : 'alcalde';
    const candidatNom = nomCandidatsMultiple ?? candidate?.nom;
    const candidatId = nomPartitsMultiple ?? partitId ?? candidate?.id;

    if (conditional) {
        const key = isPreferent ? 'msgCondPreferent' : 'msgCondNoPreferent';
        const params = isPreferent
            ? { candidat: candidatNom, verb: futureVerb, title, regidors }
            : { partit: candidatId, verb: futureVerbAlt, regidors };
        return { msg: getMessagesText(tipus, key, params), fotoCandidat: candidate?.foto };
    }

    const keyBase = isPreferent ? 'msgPreferent' : 'msgNoPreferent';
    const candidat = isPreferent ? candidatNom : candidatId;
    const verb = isPreferent ? futureVerb : futureVerbAlt;

    let key;
    const params = { candidat, verb, regidors };

    if (isPacte) {
        if (altCandidateName) {
            key = `${keyBase}PacteAlternatiu`;
            params.candidatAlt = altCandidateName;
        } else {
            key = `${keyBase}Pacte`;
        }
        if (isPreferent) {
            params.title = title;
        }
        return { msg: getMessagesText(tipus, key, params), fotoCandidat: !nomCandidatsMultiple && candidate?.foto };
    }
};

const getMessageMunicipals = (tipus, selections, partits = [], minValue, preferent, percEscrutat) => {
    const filtrats = partits.filter((item) => item.mnumitems >= minValue);
    let majoriaAbsoluta =
        filtrats.length > 0 ? filtrats.reduce((max, item) => (item.mnumitems >= max.mnumitems ? item : max)) : partits[0] || null;
    const pactePartits = partits.filter((p) => selections[p.id] === 'Si');
    const maxRegidors = Math.max(...pactePartits.map((p) => p.mnumitems));
    const partitsPacteMax = pactePartits.filter((p) => p.mnumitems === maxRegidors);

    const candidatsPacte = partitsPacteMax.map((p) => p.candidat?.nom ?? p.candidat?.name);
    const partitsPacte = partitsPacteMax.map((p) => p.id);

    const totalVotsSi = partits.reduce((acc, p) => (selections[p.id] === 'Si' ? acc + p.mnumitems : acc), 0);
    const partitMesVots = partits.reduce((max, item) => (item.mnumitems > max.mnumitems ? item : max));

    const nomCandidatsMultiple = candidatsPacte.length > 1 && totalVotsSi >= minValue ? candidatsPacte.join(' o ') : null;
    const nomPartitsMultiple = partitsPacte.length > 1 && totalVotsSi >= minValue ? partitsPacte.join(' o ') : null;

    const isFinal = percEscrutat > 90;

    if (majoriaAbsoluta?.mnumitems >= minValue) {
        const majoriaCandidat = majoriaAbsoluta?.candidat;
        const hasCandidateInfo = !!(majoriaCandidat?.nom && majoriaCandidat?.foto);

        if (hasCandidateInfo) {
            return {
                msg: getMessagesText(tipus, 'msgMajoriaAbsolutaCandidat', {
                    candidat: majoriaCandidat.nom,
                    title: majoriaCandidat?.genere === 'DONA' ? 'alcaldessa' : 'alcalde',
                    regidors: majoriaAbsoluta.mnumitems
                }),
                fotoCandidat: majoriaCandidat.foto
            };
        }

        return {
            msg: getMessagesText(tipus, 'msgMajoriaAbsolutaPartit', {
                partit: majoriaAbsoluta.id || majoriaAbsoluta.name,
                regidors: majoriaAbsoluta.mnumitems
            })
        };
    }

    if (totalVotsSi >= minValue) {
        const pactePartits = partits.filter((p) => selections[p.id] === 'Si');
        const partitPacteMesVots = pactePartits.reduce(
            (max, item) => (item.mnumitems > max.mnumitems ? item : max),
            pactePartits[0]
        );
        const isAltPacte = !pactePartits.some((p) => p.id.includes(partitMesVots.id));
        const altCandidateName = isAltPacte ? partitMesVots?.candidat?.nom : '';

        return getMessageCandidat({
            tipus,
            candidate: partitPacteMesVots?.candidat ?? partitPacteMesVots,
            isPreferent: preferent,
            isFinal,
            regidors: minValue,
            isPacte: true,
            altCandidateName,
            nomCandidatsMultiple,
            nomPartitsMultiple
        });
    }
    return getMessageCandidat({
        tipus,
        candidate: partitMesVots?.candidat ?? partitMesVots,
        partitId: partitMesVots?.id,
        isPreferent: preferent,
        isFinal,
        regidors: minValue,
        conditional: true,
        nomCandidatsMultiple
    });
};

const normalizeMunicipisItems = (items) => {
    if (Array.isArray(items)) return items;
    if (Array.isArray(items?.item)) return items.item;
    if (Array.isArray(items?.items)) return items.items;
    return [];
};

const SelectorMunicipi = ({ items = [], initialMunicipi, onSelect }) => {
    const municipisItems = useMemo(() => normalizeMunicipisItems(items), [items]);
    const [searchValue, setSearchValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [selectedMunicipi, setSelectedMunicipi] = useState(
        () => municipisItems.find((item) => item.id === initialMunicipi) ?? null
    );
    const [isTyping, setIsTyping] = useState(false);
    const inputRef = useRef(null);
    const wrapperRef = useRef(null);

    const filtered = useMemo(() => {
        if (!searchValue) return municipisItems;
        return municipisItems.filter(({ nom }) =>
            normalizeText(nom).toLowerCase().includes(normalizeText(searchValue).toLowerCase())
        );
    }, [searchValue, municipisItems]);

    const handleSelect = (municipi) => {
        setSelectedMunicipi(municipi);
        setSearchValue('');
        setIsTyping(false);
        setIsOpen(false);
        inputRef.current?.blur();
        onSelect?.(municipi);
    };

    const handleClickOutside = useCallback((e) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
            setIsOpen(false);
            setIsTyping(false);
        }
    }, []);

    useEffect(() => {
        const found = municipisItems.find((item) => item.id === initialMunicipi) ?? null;
        setSelectedMunicipi(found);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialMunicipi, municipisItems]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleClickOutside]);

    return (
        <div className={styles.selectorWrapper} ref={wrapperRef}>
            <div className={styles.inputWrapper}>
                <input
                    ref={inputRef}
                    type='search'
                    value={searchValue}
                    placeholder={!isTyping && selectedMunicipi ? selectedMunicipi.nom : 'Escull un municipi'}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onFocus={() => {
                        setIsOpen(true);
                        setIsTyping(true);
                    }}
                    onClick={() => {
                        setIsOpen(true);
                        setIsTyping(true);
                    }}
                    onBlur={() => {
                        setIsTyping(false);
                    }}
                    className={styles.searchInput}
                    autoComplete='off'
                    data-testid='municipi-input-pactometre'
                />
                <span className={clsx(styles.chevronIcon, { [styles.open]: isOpen })} aria-hidden='true'>
                    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path
                            d='M6 9L12 15L18 9'
                            stroke='currentColor'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                    </svg>
                </span>
            </div>

            {isOpen && (
                <ul className={styles.dropdownList} role='listbox'>
                    {filtered.length === 0 ? (
                        <li className={styles.noResults}>No s&apos;han trobat resultats</li>
                    ) : (
                        filtered.map((item) => (
                            <li
                                key={item.id}
                                className={`${styles.dropdownItem} ${selectedMunicipi?.id === item.id ? styles.selected : ''}`}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSelect(item);
                                }}
                                role='option'
                                aria-selected={selectedMunicipi?.id === item.id}
                            >
                                {item.nom}
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    );
};

const PactometreContent = (props) => {
    const {
        urlDades,
        urlBase,
        pathLogosPartits,
        mostrarLogoPartit,
        pathCandidats,
        tipus,
        intervalRefresh = 0,
        fase,
        isIframe = false,
        municipis,
        initialMunicipi,
        selectedMunicipiId,
        onMunicipiChange,
        tabId,
        tabOptions = [],
        selectedTabIndex = 0,
        setSelectedTabIndex = () => {},
        mostraEscrutini = false
    } = props;

    const { isMobile } = useViewport();
    const chartRef = useRef(null);
    const { dataActual: data, getDades } = useEleccions();
    const resolvedUrlDades = useMemo(() => {
        if (!__isEmpty(tabOptions)) {
            return tabOptions[selectedTabIndex]?.urlDades || tabOptions[0]?.urlDades || urlDades;
        }

        if (selectedMunicipiId && urlBase) {
            return urlBase.replace('[id]', selectedMunicipiId);
        }

        return urlDades;
    }, [tabOptions, selectedTabIndex, selectedMunicipiId, urlBase, urlDades]);

    const [urlDadesActual, setUrlDadesActual] = useState(resolvedUrlDades);
    const partitsEmpty = Array.from({ length: 8 }, (_, index) => ({
        id: '-',
        name: '-',
        numitems: '-',
        mnumitems: 0,
        isEmpty: true
    }));
    const [partits, setPartits] = useState([]);
    const [totalEscons, setTotalEscons] = useState();
    const [percEscrutat, setPercEscrutat] = useState();
    const [preferent, setPreferent] = useState();
    const [selections, setSelections] = useState({});
    const [total, setTotal] = useState(0);
    const [messageResult, setMessageResult] = useState();
    const [imatgeCandidat, setImatgeCandidat] = useState();
    const [isChangingTab, setIsChangingTab] = useState(false);

    const textValue = tipus === 'municipals' ? 'regidors' : 'escons';
    const options = tipus === 'municipals' ? optionsButtonsMunicipals : optionsButtonsAutonomiques;
    const mostraEscrutiniEffective = !__isEmpty(tabOptions) ? tabOptions[selectedTabIndex]?.mostraEscrutini : mostraEscrutini;
    const isPercentatgeTab = !__isEmpty(tabOptions) && !!tabOptions[selectedTabIndex]?.percentatge;
    const effectiveTotalEscons = isPercentatgeTab ? 100 : totalEscons;
    const minValue = effectiveTotalEscons ? Math.floor(0.5 * effectiveTotalEscons) + 1 : 0;
    const textExtraPeuActual = !__isEmpty(tabOptions) ? tabOptions[selectedTabIndex]?.textExtraPeu : null;
    const formatDisplayValue = (value) => {
        if (value === null || value === undefined || value === '') return '-';

        const stringValue = String(value).trim();
        const numericValue = Number(stringValue);

        if (Number.isNaN(numericValue)) {
            return isPercentatgeTab ? `${stringValue}%` : stringValue.replace('-', ' - ');
        }

        const roundedValue = Math.round((numericValue + Number.EPSILON) * 100) / 100;
        const formatted = Number.isInteger(roundedValue)
            ? String(roundedValue)
            : roundedValue
                  .toFixed(2)
                  .replace(/\.?0+$/, '')
                  .replace('.', ',');

        return isPercentatgeTab ? `${formatted}%` : formatted.replace('-', ' - ');
    };
    const resetPactometre = () => {
        setSelections({});
        setTotal({
            si: 0,
            no: 0,
            abs: 0
        });
    };

    useEffect(() => {
        if (!__isEmpty(tabOptions)) {
            setIsChangingTab(true);
            setUrlDadesActual((prev) => (prev === resolvedUrlDades ? prev : resolvedUrlDades));
            resetPactometre();
            return;
        }

        if (!selectedMunicipiId || !urlBase) return;
        const nextUrl = urlBase.replace('[id]', selectedMunicipiId);
        setIsChangingTab(true);
        setUrlDadesActual((prev) => (prev === nextUrl ? prev : nextUrl));
        resetPactometre();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMunicipiId, urlBase, tabOptions, selectedTabIndex, resolvedUrlDades]);

    const getMessage = () => {
        switch (tipus) {
            case 'municipals':
                return getMessageMunicipals(tipus, selections, partits, minValue, preferent, percEscrutat);
            case 'autonomiques':
                return getMessageAutonomiques(tipus, selections, partits, minValue);
            case 'generals':
                return getMessageAutonomiques(tipus, selections, partits, minValue);
        }
    };

    useEffect(() => {
        getDades({
            urlDades: urlDadesActual,
            intervalRefresh
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlDadesActual]);

    useEffect(() => {
        if (data) {
            setTotalEscons(isPercentatgeTab ? 100 : data.datos?.geo?.num_items_total);
            setPercEscrutat(data.datos?.geo?.perc_escrutat);
            setPreferent(data.datos?.geo?.preferent);
            const partitsFiltrats = data.datos?.geo?.opcions?.item?.filter((item) =>
                fase === 'sondeig' ? item.mnumitems > 0 : item.numitems > 0
            );
            setPartits(partitsFiltrats || []);
            setIsChangingTab(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (!__isEmpty(partits)) {
            setTotal({
                si: partits.reduce((acc, partit) => {
                    return selections[partit.id] === 'Si' ? acc + partit.mnumitems : acc;
                }, 0),
                no: partits.reduce((acc, partit) => {
                    return selections[partit.id] === 'No' ? acc + partit.mnumitems : acc;
                }, 0),
                abs: partits.reduce((acc, partit) => {
                    return selections[partit.id] === 'Abs' ? acc + partit.mnumitems : acc;
                }, 0)
            });
            if (chartRef.current) {
                chartRef.current.resize();
            }
            setMessageResult(getMessage());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selections, partits]);

    useEffect(() => {
        let imgCandidat = null;
        if (messageResult?.fotoCandidat)
            imgCandidat = `${pathCandidats}${messageResult.fotoCandidat.replace(/[^a-zA-Z0-9_-\s]/g, '')}.jpg`;
        setImatgeCandidat(imgCandidat);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messageResult]);

    const selectedIds = new Set(
        Object.entries(selections)
            .filter(([_, value]) => value === 'Si')
            .map(([key]) => key)
    );

    const safePartits = Array.isArray(partits) ? partits : [];

    const hasSelections = selectedIds.size > 0;
    const partitsSi = safePartits.filter((p) => selections[p.id] === 'Si');
    const partitsNo = safePartits.filter((p) => selections[p.id] === 'No');
    const totalSi = partitsSi.reduce((acc, p) => acc + p.mnumitems, 0);
    const totalNo = partitsNo.reduce((acc, p) => acc + p.mnumitems, 0);
    const gris = data ? totalEscons - totalSi - totalNo : 100;
    const datasets = [];
    partitsSi.forEach((p) => {
        datasets.push({
            label: p.id,
            data: [p.mnumitems],
            backgroundColor: p.rgbcolor,
            stack: 'total'
        });
    });
    datasets.push({
        label: 'disabled',
        data: [gris],
        backgroundColor: '#DCE3E5',
        stack: 'total',
        hoverBackgroundColor: '#DCE3E5',
        hoverBorderColor: '#DCE3E5',
        hoverBorderWidth: 0,
        borderWidth: 0,
        events: []
    });
    partitsNo.forEach((p) => {
        datasets.push({
            label: p.id,
            data: [p.mnumitems],
            backgroundColor: p.rgbcolor,
            stack: 'total'
        });
    });

    const handleChange = (id, selection) => {
        setSelections((prev) => ({
            ...prev,
            [id]: selection
        }));
    };

    const totalValue = totalEscons || 0;
    const majoritySeats = totalEscons / 2;
    const majorityPercent = totalEscons ? (majoritySeats / totalEscons) * 100 : 50;
    const barGris = totalValue - total?.si - total?.no;
    const getPercent = (value) => {
        if (!totalValue) return 0;
        return (value / totalValue) * 100;
    };

    const optionsChart = isMobile
        ? {
              layout: {
                  padding: {
                      top: 8
                  }
              }
          }
        : null;

    const stylesPactometre = clsx({
        [styles.pactometreWrapper]: true,
        [styles.empty]: !data
    });
    const stylesPactometresContainer = clsx({
        [styles.pactometresContainer]: true,
        [styles.isChangingTab]: isChangingTab
    });
    const stylesTextResultat = clsx({
        [styles.resultatText]: true,
        [styles.disabled]: total === 0
    });
    const stylesResultatGrafic = clsx(styles.resultatGrafic, {
        [styles.resultatGraficWithEscrutini]: !mostraEscrutiniEffective
    });
    const stylesPartit = clsx({
        [styles.partit]: true,
        [styles.partitSenseLogo]: !mostrarLogoPartit && data
    });
    const partitsToRender = data ? partits : partitsEmpty;
    const useTwoColumnsInPartits = useMemo(
        () => partitsToRender.some((partit) => !partit?.isEmpty && String(partit?.id || '').length > 16),
        [partitsToRender]
    );
    const stylesPartitsWrapper = clsx(styles.partitsWrapper, {
        [styles.partitsWrapperTwoColumns]: useTwoColumnsInPartits
    });
    const entradeta =
        tipus === 'municipals'
            ? 'Guanyarà l’alcaldia qui tingui majoria absoluta. Si no, qui tingui més regidors, llevat que algun altre candidat aconsegueixi un pacte alternatiu amb majoria absoluta.'
            : 'Marca “sí” o “no” a cada partit i comprova com canvia l’escenari polític.<br />Accedirà a la presidència qui hagi obtingut majoria absoluta. Si no, qui tingui majoria simple en una segona votació.';
    return (
        <div className={stylesPactometre}>
            {!isIframe && (
                <div className={styles.capcalera}>
                    <div className={styles.titolWrapper}>
                        <div className={styles.titol}>
                            Fes les teves aliances per crear <span>govern</span>
                        </div>
                        <Text text={entradeta} className={styles.entradeta} />
                    </div>
                    {!__isEmpty(tabOptions) && (
                        <div className={styles.tabSelectorContainer}>
                            <TabSelector
                                key={`selector-pactometre-${tabId}`}
                                tabId={tabId}
                                items={tabOptions.map((tab, index) => ({ boto: tab.label, valor: index }))}
                                selectedIndex={selectedTabIndex}
                                onChange={(index) => setSelectedTabIndex(index)}
                                estils='pb-0'
                                estilBotons='petit'
                            />
                        </div>
                    )}
                </div>
            )}
            <div className={stylesPactometresContainer}>
                {tipus === 'municipals' && (
                    <div className={styles.selectorMunicipis}>
                        <SelectorMunicipi
                            items={municipis}
                            initialMunicipi={selectedMunicipiId || initialMunicipi}
                            onSelect={(municipi) => {
                                const novaUrl = urlBase.replace('[id]', municipi.id);
                                onMunicipiChange?.(municipi.id, municipi);
                                setUrlDadesActual(novaUrl);
                                resetPactometre();
                            }}
                        />
                    </div>
                )}
                <div className={styles.pactometre}>
                    <div className={styles.candidatCard}>
                        <div className={styles.candidatImatgeWrapper}>
                            <div className={styles.candidatImatge}>
                                {imatgeCandidat ? (
                                    <div className={styles.imatgeCandidat}>
                                        <Imatge
                                            key={`imatge-candidat-${imatgeCandidat}`}
                                            src={imatgeCandidat}
                                            layout='fill'
                                            objectFit='cover'
                                        />
                                    </div>
                                ) : (
                                    <Imatge
                                        src='/_resources/imatges/3catinfo/candidat-pactometre.png'
                                        tipus='web_candidat_pactometre'
                                        layout='fill'
                                        mostrarRecursTitol={false}
                                        mostrarRecurs={false}
                                    />
                                )}
                            </div>
                            {isMobile && (
                                <div className={stylesTextResultat}>
                                    <Text text={messageResult?.msg || 'Comença a fer els teus pactes'} tag='div' />
                                </div>
                            )}
                        </div>
                        <div className={styles.resultatWrapper}>
                            {!isMobile && (
                                <div className={stylesTextResultat}>
                                    <Text
                                        className={styles.textRes}
                                        text={messageResult?.msg || 'Comença a fer els teus pactes'}
                                        tag='div'
                                    />
                                </div>
                            )}
                            {mostraEscrutiniEffective && <div className={styles.escrutini}>Escrutini: {percEscrutat}%</div>}
                            <div className={stylesResultatGrafic}>
                                <div className={styles.lineVertical} style={{ left: `${majorityPercent}%` }} />
                                <div className={styles.graficBarra}>
                                    <GraficBarraHoritzontal
                                        key={`${totalEscons}-${minValue}-${partits?.length}-${total?.si}-${total?.no}`}
                                        chartRef={chartRef}
                                        datasets={datasets}
                                        maxValue={totalEscons}
                                        options={optionsChart}
                                        showTooltip={hasSelections}
                                        paddingTop={0}
                                        customTooltipFilter={(tooltipItem) => tooltipItem.dataset.label !== 'disabled'}
                                    />
                                </div>
                                <div className={styles.resumContent}>
                                    <div className={styles.barWrapper}>
                                        <div
                                            className={`${styles.segment} ${styles.segmentSi}`}
                                            style={{
                                                width: `${getPercent(total?.si)}%`
                                            }}
                                        />
                                        <div
                                            className={`${styles.segment} ${styles.segmentDisabled}`}
                                            style={{
                                                width: `${getPercent(barGris)}%`
                                            }}
                                        />
                                        <div
                                            className={`${styles.segment} ${styles.segmentNo}`}
                                            style={{
                                                width: `${getPercent(total?.no)}%`
                                            }}
                                        />
                                    </div>
                                </div>
                                {tipus === 'municipals' && (
                                    <div className={styles.llegendaGrafic}>
                                        <div className={styles.llegendaGrup}>
                                            <div>
                                                <span className={styles.label}>Regidors:</span>{' '}
                                                <span className={styles.value}>
                                                    {total?.si ? formatDisplayValue(total?.si) : '-'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className={styles.grupMajoria}>
                                            <span className={styles.label}>Majoria absoluta:</span>
                                            {!isPercentatgeTab && (
                                                <span className={styles.value}>
                                                    {totalEscons ? ` ${minValue} de ${totalEscons} ${textValue}` : ' -'}
                                                </span>
                                            )}
                                        </div>
                                        <div className={styles.llegendaGrup}></div>
                                    </div>
                                )}
                                {tipus !== 'municipals' && (
                                    <div className={styles.llegendaGrafic}>
                                        <div className={styles.llegendaGrup}>
                                            <div className={styles.llegendaSi}></div>
                                            <div>
                                                <span className={styles.label}>Sí:</span>{' '}
                                                <span className={styles.value}>
                                                    {' '}
                                                    {total?.si ? formatDisplayValue(total?.si) : '-'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className={styles.grupMajoria}>
                                            <span className={styles.label}>Majoria absoluta{!isPercentatgeTab ? ':' : ''}</span>
                                            {!isPercentatgeTab && (
                                                <span className={styles.value}>
                                                    {data ? ` ${minValue} de ${totalEscons} ${textValue}` : ' -'}
                                                </span>
                                            )}
                                        </div>
                                        <div className={styles.llegendaGrup}>
                                            <div className={styles.llegendaNo}></div>
                                            <div>
                                                <span className={styles.label}>No:</span>{' '}
                                                <span className={styles.value}>
                                                    {total?.no ? formatDisplayValue(total?.no) : '-'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={stylesPartitsWrapper}>
                        {partitsToRender?.map((partit, index) => {
                            const imgPartit = partit.isEmpty
                                ? `https://statics.3cat.cat/recursos/3catinfo/img/eleccions/logo-partit-empty.png`
                                : `${pathLogosPartits}${String(partit?.id || '')
                                      .replace(/[^a-zA-Z0-9_-\s]/g, '')
                                      .replace(/\s+/g, '')}.jpg`;
                            return (
                                <div key={`pactometre-partit-${index}`} className={styles.partitWrapper}>
                                    <div className={stylesPartit}>
                                        {mostrarLogoPartit || partit?.isEmpty ? (
                                            <div className={styles.logoPartit}>
                                                <Imatge src={imgPartit} layout='fill' objectFit='cover' />
                                            </div>
                                        ) : (
                                            <div
                                                className={styles.boletaPartit}
                                                style={{
                                                    backgroundColor: partit?.rgbcolor
                                                }}
                                            ></div>
                                        )}
                                        <div className={styles.infoPartit}>
                                            <div className={styles.nomPartit}>{partit.id}</div>
                                            <div className={styles.esconsPartit}>{formatDisplayValue(partit.numitems)}</div>
                                        </div>
                                    </div>
                                    <div className={styles.selectorPartit}>
                                        <SelectButton
                                            id={partit.id}
                                            isEmpty={partit.isEmpty}
                                            options={options}
                                            current={selections[partit.id]}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className={styles.rowBottom}>
                <div>
                    <Boto
                        className={__isEmpty(selections) ? styles.btnDisabled : ''}
                        isIconaDreta={false}
                        iconaInline={true}
                        onClick={() => {
                            resetPactometre();
                        }}
                        tag='button'
                        text='Nou pacte'
                        icona='tornaaveure'
                    />
                </div>
                {(fase === 'sondeig' || !__isEmpty(textExtraPeuActual)) && (
                    <div className={styles.textSondeig}>
                        {fase === 'sondeig' && 'Els pactes es fan tenint en compte el valor mitjà de les forquilles.'}
                        {!__isEmpty(textExtraPeuActual) && (fase === 'sondeig' ? ` ${textExtraPeuActual}` : textExtraPeuActual)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default function Pactometre(props) {
    const { tabs, tabsData, tabId = 'pactometre-tabs' } = props;
    const tabOptions = useMemo(() => {
        const source = Array.isArray(tabs) ? tabs : Array.isArray(tabsData) ? tabsData : [];
        if (__isEmpty(source)) return [];

        return source.map((tab, index) => ({
            ...tab,
            label: tab?.label || tab?.boto || tab?.nom || `Tab ${index + 1}`,
            urlDades: tab?.urlDades || tab?.url || props.urlDades
        }));
    }, [props, tabs, tabsData]);

    const [selectedTabIndex, setSelectedTabIndex] = useState(0);

    useEffect(() => {
        if (!__isEmpty(tabOptions) && selectedTabIndex >= tabOptions.length) {
            setSelectedTabIndex(0);
        }
    }, [selectedTabIndex, tabOptions]);

    if (!__isEmpty(tabOptions)) {
        return (
            <PactometreContent
                {...props}
                tabId={tabId}
                tabOptions={tabOptions}
                selectedTabIndex={selectedTabIndex}
                setSelectedTabIndex={setSelectedTabIndex}
            />
        );
    }

    return <PactometreContent {...props} />;
}
